import {
  useCallback, useMemo, useRef, 
} from 'react';
import * as is from 'is';
import { getZones, saveZone } from '@utils/graphql/creators/zones';

/**
 * A hook for converting device headers into data.
 *
 * Returns a function for creating a device, retrieving a device, and a list of the
 * device headers.
 *
 * @returns [function, function, string[]]
 */
export const useZoneConverter = () => {
  const savedZones = useRef(new Map());
  // a mapping of device header names to properties, as well as a function to process
  // the header value, if required.
  // the mapping is a plain object with the following properties:
  // - property (required): this is the GraphQL property name to use with the header name.
  // - processor (optional): a function to call for processing the header value.
  const headerMapping = useMemo(() => {
    return {
      'Zone Name': {
        property: 'name',
      },
      'Zone Description': {
        property: 'description',
      },
    };
  }, []);
  const headerColumns = useMemo(() => {
    return Object.keys(headerMapping);
  }, [headerMapping]);

  const getMapping = useCallback(
    /**
     * A function for retrieving the mapping information for a column.
     *
     * If the column name has an associated mapping (i.e.: plain object), it will be returned;
     * otherwise, undefined will be returned
     * @param {string} columnName The name of the column to use.
     * @returns {({property, processor?} | undefined)}
     */
    (columnName) => {
      const response = headerMapping[columnName];
      if (is.object(response)) {
        return response;
      }
    },
    [headerMapping]
  );
  const getReverseMapping = useCallback(
    /**
     * Given a GraphQL property name, this function will return the header names which map to it.
     *
     * @param {string} property The name of the property to search for.
     * @returns {string[]}
     */
    (property) => {
      const columnNames = [];
      for (const [columnName, properties] of Object.entries(headerMapping)) {
        if (properties.property === property) {
          columnNames.push(columnName);
        }
      }
      return columnNames;
    },
    [headerMapping]
  );
  const storeZone = useCallback(
    /**
     * Stores a saved/cached copy of the device by device name.
     *
     * This is to prevent many calls to the SSoT during bulk import.
     *
     * @param {object} zone The source object retrieved from the SSoT.
     * @returns {void}
     */
    (zone) => {
      if (savedZones.current.has(zone.name)) {
        return;
      }
      savedZones.current.set(zone.name, zone);
    },
    [savedZones]
  );
  const retrieveZone = useCallback(
    /**
     * Retrieves a zone by name.
     *
     * The function will first check the internal cache for the requested zone. If it is found the that zone
     * will be returned. Otherwise, it will check the SSoT for the corresponding zone. If matches are found,
     * the first will be returned.
     *
     * If not matches are found, then undefined is returned.
     *
     * @param {string} name The name of the zone to retrieve.
     * @returns {(object | undefined)}
     */
    async (name) => {
      if (is.undef(name)) {
        return;
      }
      if (savedZones.current.has(name)) {
        return savedZones.current.get(name);
      }
      const { query, execute } = getZones(null, { name: name });
      query.addReturnProperties('id', 'name');
      const response = await execute();
      if (response.error) {
        return;
      }
      if (response.data.length > 0) {
        const device = response.data[0];
        savedZones.current.set(device.name, device);
        return device;
      }
    },
    [savedZones]
  );
  const convert = useCallback(
    /**
     * Processes a representation of the current row being evaluated.
     *
     * @param {object} row An object containing the header names and the associated values.
     * @returns {Promise.<object>}
     */
    async (row) => {
      const input = {};
      for (const [header, value] of Object.entries(headerMapping)) {
        const { property, processor } = value;
        const rowValue = row[header];
        const _value = is.function(processor)
          ? await processor(row, input, rowValue)
          : rowValue;

        if (_value === undefined) {
          continue;
        }
        input[property] = _value;
      }
      return input;
    },
    [headerMapping]
  );
  const createZone = useCallback(
    /**
     * Creates or retrieves a device using the row data.
     *
     * The function first checks if the device is in the cache or the SSoT. If not, it constructs
     * an input object to create the device.
     *
     * @param {object} row An object containing the header names and the associated values.
     * @returns {Promise.<object>}
     */
    async (row) => {
      const names = getReverseMapping('name');
      for (const headerName of names) {
        const device = await retrieveZone(row[headerName]);
        if (is.defined(device)) {
          return device;
        }
      }
      const input = await convert(row, getMapping);
      const { mutation, execute } = saveZone(undefined, input);
      mutation.addReturnProperties('id', 'name');
      const response = await execute();
      if (response.error) {
        throw new Error(response.error);
      }
      storeZone(response.data);
      return response.data;
    },
    [
      storeZone,
      retrieveZone,
      getReverseMapping,
      getMapping,
      convert,
    ]
  );
  return [
    createZone,
    retrieveZone,
    headerColumns,
  ];
};
